import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Copy, Check, Trash2, FileText, Download, Image as ImageIcon, Video, Archive, FileSpreadsheet, File } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
const FileManagement = () => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [description, setDescription] = useState("");
    const [copiedId, setCopiedId] = useState(null);
    const [filter, setFilter] = useState("all");
    const { toast } = useToast();
    useEffect(() => {
        fetchFiles();
    }, [filter]);
    const fetchFiles = async () => {
        try {
            const token = localStorage.getItem('token');
            const url = filter === 'all'
                ? `${API_URL}/files`
                : `${API_URL}/files?category=${filter}`;
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setFiles(data);
            }
        }
        catch (error) {
            console.error('Error fetching files:', error);
            toast({
                title: "Error",
                description: "Failed to fetch files",
                variant: "destructive"
            });
        }
    };
    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };
    const handleUpload = async () => {
        if (!selectedFile) {
            toast({
                title: "No file selected",
                description: "Please select a file to upload",
                variant: "destructive"
            });
            return;
        }
        setUploading(true);
        const formData = new FormData();
        formData.append('file', selectedFile);
        if (description) {
            formData.append('description', description);
        }
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/files/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });
            if (response.ok) {
                const data = await response.json();
                toast({
                    title: "Success!",
                    description: (_jsxs("div", { className: "space-y-2", children: [_jsx("p", { children: "File uploaded successfully!" }), _jsx("div", { className: "bg-muted p-2 rounded font-mono text-sm break-all", children: data.publicUrl })] }))
                });
                setSelectedFile(null);
                setDescription("");
                fetchFiles();
                // Reset file input
                const fileInput = document.getElementById('file-input');
                if (fileInput)
                    fileInput.value = '';
            }
            else {
                const error = await response.json();
                throw new Error(error.error || 'Upload failed');
            }
        }
        catch (error) {
            toast({
                title: "Upload failed",
                description: error.message,
                variant: "destructive"
            });
        }
        finally {
            setUploading(false);
        }
    };
    const copyToClipboard = (url, id) => {
        navigator.clipboard.writeText(url);
        setCopiedId(id);
        toast({
            title: "Copied!",
            description: "URL copied to clipboard"
        });
        setTimeout(() => setCopiedId(null), 2000);
    };
    const deleteFile = async (id) => {
        if (!confirm('Are you sure you want to delete this file?'))
            return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/files/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                toast({
                    title: "Deleted",
                    description: "File deleted successfully"
                });
                fetchFiles();
            }
        }
        catch (error) {
            toast({
                title: "Error",
                description: "Failed to delete file",
                variant: "destructive"
            });
        }
    };
    const formatFileSize = (bytes) => {
        if (bytes < 1024)
            return bytes + ' B';
        if (bytes < 1024 * 1024)
            return (bytes / 1024).toFixed(2) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };
    const getFileIcon = (category) => {
        switch (category) {
            case 'image': return _jsx(ImageIcon, { className: "w-5 h-5" });
            case 'video': return _jsx(Video, { className: "w-5 h-5" });
            case 'pdf': return _jsx(FileText, { className: "w-5 h-5" });
            case 'spreadsheet': return _jsx(FileSpreadsheet, { className: "w-5 h-5" });
            case 'archive': return _jsx(Archive, { className: "w-5 h-5" });
            default: return _jsx(File, { className: "w-5 h-5" });
        }
    };
    return (_jsxs("div", { className: "container mx-auto p-6 space-y-6", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-3xl font-bold mb-2", children: "File Management" }), _jsx("p", { className: "text-muted-foreground", children: "Upload files and get shareable links for your blog articles" })] }), _jsxs(Card, { children: [_jsxs(CardHeader, { children: [_jsx(CardTitle, { children: "Upload New File" }), _jsx(CardDescription, { children: "Upload PDFs, documents, images, videos, and more (max 50MB)" })] }), _jsxs(CardContent, { className: "space-y-4", children: [_jsxs("div", { children: [_jsx(Label, { htmlFor: "file-input", children: "Select File" }), _jsx(Input, { id: "file-input", type: "file", onChange: handleFileChange, className: "cursor-pointer" }), selectedFile && (_jsxs("p", { className: "text-sm text-muted-foreground mt-2", children: ["Selected: ", selectedFile.name, " (", formatFileSize(selectedFile.size), ")"] }))] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "description", children: "Description (Optional)" }), _jsx(Input, { id: "description", value: description, onChange: (e) => setDescription(e.target.value), placeholder: "e.g., Q4 2025 Report, Product Brochure..." })] }), _jsxs(Button, { onClick: handleUpload, disabled: !selectedFile || uploading, className: "w-full", children: [_jsx(Upload, { className: "w-4 h-4 mr-2" }), uploading ? 'Uploading...' : 'Upload File'] })] })] }), _jsx("div", { className: "flex gap-2 flex-wrap", children: ['all', 'pdf', 'document', 'image', 'video', 'spreadsheet', 'archive'].map((cat) => (_jsx(Button, { variant: filter === cat ? "default" : "outline", size: "sm", onClick: () => setFilter(cat), children: cat.charAt(0).toUpperCase() + cat.slice(1) }, cat))) }), _jsx("div", { className: "grid gap-4", children: files.length === 0 ? (_jsx(Card, { children: _jsx(CardContent, { className: "py-12 text-center text-muted-foreground", children: "No files uploaded yet" }) })) : (files.map((file) => (_jsx(Card, { children: _jsx(CardContent, { className: "p-4", children: _jsxs("div", { className: "flex items-start gap-4", children: [_jsx("div", { className: "p-2 bg-muted rounded", children: getFileIcon(file.category) }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("h3", { className: "font-semibold truncate", children: file.originalName }), file.description && (_jsx("p", { className: "text-sm text-muted-foreground", children: file.description })), _jsxs("div", { className: "flex gap-4 text-xs text-muted-foreground mt-1", children: [_jsx("span", { children: formatFileSize(file.size) }), _jsx("span", { children: file.category }), _jsx("span", { className: "font-medium", children: new Date(file.uploadedAt).toLocaleString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    }) })] }), _jsxs("div", { className: "mt-3 p-2 bg-muted rounded flex items-center gap-2", children: [_jsx("code", { className: "text-xs flex-1 truncate", children: file.publicUrl }), _jsx(Button, { size: "sm", variant: "ghost", onClick: () => copyToClipboard(file.publicUrl, file.id), children: copiedId === file.id ? (_jsx(Check, { className: "w-4 h-4 text-green-500" })) : (_jsx(Copy, { className: "w-4 h-4" })) })] })] }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { size: "sm", variant: "outline", asChild: true, children: _jsx("a", { href: file.publicUrl, target: "_blank", rel: "noopener noreferrer", children: _jsx(Download, { className: "w-4 h-4" }) }) }), _jsx(Button, { size: "sm", variant: "destructive", onClick: () => deleteFile(file.id), children: _jsx(Trash2, { className: "w-4 h-4" }) })] })] }) }) }, file.id)))) })] }));
};
export default FileManagement;
